/**
 * Combat schemas (D234, actors round D) — the third thing an actor does
 * (`docs/design/actors.md` §6; Mike's rulings 2, 3 and 4 of 2026-10-04 at
 * the top of its §9; Calder's `actors-content.md` §5.4).
 *
 * @remarks
 * A fight is a sequence of turns, not a dice cup. **The arithmetic is
 * fixed** (ruling 2): every strike does exactly its damage — dealt, a
 * weapon's `damage` less the foe's `defense`; taken, the foe's `damage` less
 * the sum of the player's worn `armor`; both floored at zero — and nothing
 * here reads the seeded RNG. A three-health foe and a two-damage bar are a
 * two-turn fight the player can count, and being able to count it is the
 * difference between a puzzle and a slot machine (Calder, §8). Variance, if
 * a later pack wants it, is one field (`damage: { min, max }`) drawn through
 * D141's seam; it is not here because nothing asks for it.
 *
 * Four shapes, each a fact about one thing:
 *
 * - **`combat` on an actor** — what it takes to put the body down
 *   (`health`), what it does to the player every turn it is hostile and in
 *   reach (`damage`, `defense`), WHEN it is hostile (`hostileWhen`, an
 *   ordinary condition: the drain animal's is "the mask is on"), what is
 *   said when it strikes, is struck and falls, how it reads once it has
 *   fallen (`defeatedDescription`), what its fall does to the world
 *   (`onDefeat`), and — Calder's §5.4 — which ending ITS kill selects
 *   (`deathEndingId`, falling back to the manifest's). An actor with no
 *   `combat` is not a foe: `attack` refuses in its own words (`verbNarratives`)
 *   or the `notAFoe` slot's.
 * - **`weapon` on an item** — what the thing does when swung. Nothing else;
 *   a weapon is a thing that happens to hurt.
 * - **`armor` on an item** — what the thing turns while it is WORN. Armor
 *   counts only while equipped, so it requires `equippable` (the item schema
 *   says so): armor nothing can wear is a number nothing reads.
 * - **`player` on the manifest** — the player's `health`, their
 *   `unarmedDamage` (what bare hands do; default 0) and the pack's answer to
 *   "you ran out of health with nothing in particular killing you"
 *   (`deathEndingId`). Absent, the pack has no combat and pays nothing: no
 *   health is tracked, no foe can strike, and the loader refuses a `combat`
 *   block in a pack that declares none.
 *
 * **Provocation is decided at load** (ruling 3): `attack` is allowed only on
 * an actor whose `hostileWhen` CAN become true, and the one way a pack can
 * say otherwise is a `hostileWhen` the producibility walk (D184) proves
 * never holds — which is already a refused pack. So, at runtime, a body with
 * a `combat` block is attackable whether or not it is hostile right now, and
 * the pack's hostility condition may read the act itself (`actor_attacked`)
 * to say "it turns on you when struck".
 *
 * **The checkpoint photographs health with the world** (ruling 4): the
 * player's health and every foe's are facts in `world`, so a restore puts
 * back exactly the health the player had when the photograph was taken —
 * not full — and a defeated body is standing again if it stood then.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionalTextSchema } from './conditional-text.js';
import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { endingIdSchema } from './identifiers.js';
import { nonNegativeIntegerSchema, positiveIntegerSchema } from './primitives.js';
import { narrativeLinesSchema } from './transcript.js';

/** What an actor is as a FOE — see the module remarks. */
export const actorCombatSchema = z
	.strictObject({
		/**
		 * What the foe says as it strikes the player, every turn it does —
		 * spoken before the `playerHurt` / `playerUnhurt` fact. Absent, the
		 * `foeStrikes` slot speaks.
		 */
		attackNarratives: narrativeLinesSchema.optional(),
		/**
		 * What the foe does to the player each turn it is hostile and within
		 * reach, before the player's worn armor turns any of it. Zero is a
		 * thing you strike that does not strike back — Calder's door.
		 */
		damage: nonNegativeIntegerSchema,
		/**
		 * Calder's §5.4: the ending THIS foe's kill selects, exactly as a
		 * `set_ending` would. Absent, the manifest's `player.deathEndingId` —
		 * the pack's one answer for a death nothing in particular authored.
		 * A pack with two ways to die should not say the same thing twice.
		 */
		deathEndingId: endingIdSchema.optional(),
		/**
		 * How the body reads to `examine` once it has fallen. Absent, the
		 * actor's ordinary description and fragments speak (a fragment on
		 * `actor_defeated` is the other way to say it).
		 */
		defeatedDescription: conditionalTextSchema.optional(),
		/** What the foe says when it falls — health at or below zero. Absent, the `foeDefeated` slot. */
		defeatNarratives: narrativeLinesSchema.optional(),
		/** What the foe turns of every strike against it — a weapon's `damage` less this, floored at zero. */
		defense: nonNegativeIntegerSchema.default(0),
		/** How much the body takes before it falls. */
		health: positiveIntegerSchema,
		/**
		 * WHEN the foe strikes: while this holds and the body is present
		 * (`actor_present`), it strikes once per executed turn. An ordinary
		 * condition — the drain animal's is `item_equipped item_mask`; a thing
		 * that turns on you when struck reads `actor_attacked`. The loader's
		 * producibility walk refuses one that can never hold: a foe that can
		 * never be hostile is one `attack` could never be allowed on.
		 */
		hostileWhen: conditionSchema,
		/** What being STRUCK says, when the strike costs it something. Absent, the `strikeLanded` slot. A strike it turns entirely speaks `strikeAbsorbed`. */
		hurtNarratives: narrativeLinesSchema.optional(),
		/** What the fall does to the world — after `defeatNarratives`, in order, wherever effects live. */
		onDefeat: z.array(effectSchema).readonly().default([]),
	})
	.readonly();

/** One actor's combat block — see {@link actorCombatSchema}. */
export type ActorCombat = z.infer<typeof actorCombatSchema>;

/** What a thing does when it is swung — see the module remarks. */
export const weaponSchema = z
	.strictObject({
		/** What a strike with this thing costs the foe, before the foe's `defense`. */
		damage: nonNegativeIntegerSchema,
	})
	.readonly();

/** One item's weapon declaration — see {@link weaponSchema}. */
export type Weapon = z.infer<typeof weaponSchema>;

/** What a thing turns while it is WORN — see the module remarks. */
export const armorSchema = z
	.strictObject({
		/** What this turns of every strike against the player while it is equipped — summed over everything worn. */
		defense: positiveIntegerSchema,
	})
	.readonly();

/** One item's armor declaration — see {@link armorSchema}. */
export type Armor = z.infer<typeof armorSchema>;

/** The player's body, declared on the manifest — see the module remarks. */
export const playerCombatSchema = z
	.strictObject({
		/**
		 * The ending selected when the player's health reaches zero and the
		 * foe that did it names none of its own — as `set_ending` would select
		 * it: terminal or not, `restartAt: 'checkpoint'` if the pack says so.
		 * Required with `health`, because a health that reaches zero and does
		 * nothing is a lie the gauge would tell if there were one.
		 */
		deathEndingId: endingIdSchema,
		/** How much the player can take. The checkpoint photographs what is left of it with the world. */
		health: positiveIntegerSchema,
		/** What bare hands do — the strike with no weapon in them. Default nothing. */
		unarmedDamage: nonNegativeIntegerSchema.default(0),
	})
	.readonly();

/** The manifest's player block — see {@link playerCombatSchema}. */
export type PlayerCombat = z.infer<typeof playerCombatSchema>;
